import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface QuickFiltersProps {
  onFilterChange: (filters: ChargePointFilters) => void;
  hasAC: boolean;
  hasDC: boolean;
  sortBy: 'power' | 'price' | 'name' | 'status';
  onSortChange: (sort: 'power' | 'price' | 'name' | 'status') => void;
}

export interface ChargePointFilters {
  status: 'all' | 'available';
  type: 'all' | 'AC' | 'DC';
  search: string;
}

/**
 * Compact single-line filter bar with expandable search, status, type, and sort
 */
export function QuickFilters({
  onFilterChange,
  hasAC,
  hasDC,
  sortBy,
  onSortChange,
}: QuickFiltersProps) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ChargePointFilters>({
    status: 'all',
    type: 'all',
    search: '',
  });
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const handleStatusChange = (status: ChargePointFilters['status']) => {
    const newFilters = { ...filters, status };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleTypeChange = (type: ChargePointFilters['type']) => {
    const newFilters = { ...filters, type };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSearchChange = (search: string) => {
    const newFilters = { ...filters, search };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSearchClose = () => {
    if (!filters.search) {
      setIsSearchExpanded(false);
    }
  };

  const handleSearchClear = () => {
    handleSearchChange('');
    setIsSearchExpanded(false);
  };

  // Don't show type filter if only one type exists
  const showTypeFilter = hasAC && hasDC;

  return (
    <div className="flex items-center gap-2 bg-ink/[0.03] p-2 rounded-lg border border-ink/10">
      {/* Expandable Search */}
      {isSearchExpanded ? (
        <div className="relative flex-1">
          <input
            type="text"
            placeholder={t('common.search') || 'Search...'}
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            onBlur={handleSearchClose}
            autoFocus
            className="w-full px-2.5 py-1.5 pl-7 pr-7 text-xs border border-ink/10 rounded-md bg-surface focus:outline-none focus:ring-1 focus:ring-ink focus:border-transparent placeholder:text-muted/70"
          />
          <svg
            className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted/70"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <button
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent blur on input
              handleSearchClear();
            }}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-ink/5 transition-colors"
            aria-label="Clear search"
          >
            <svg
              className="w-3 h-3 text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      ) : (
        <>
          {/* Search Icon Button */}
          <button
            onClick={() => setIsSearchExpanded(true)}
            className="p-1.5 rounded hover:bg-surface transition-colors flex-shrink-0"
            title={t('common.search')}
          >
            <svg
              className="w-4 h-4 text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>

          {/* Status Filter Dropdown */}
          <select
            value={filters.status}
            onChange={(e) =>
              handleStatusChange(e.target.value as ChargePointFilters['status'])
            }
            className="text-xs border border-ink/10 rounded-md px-2 py-1.5 bg-surface focus:outline-none focus:ring-1 focus:ring-ink focus:border-transparent appearance-none pr-6 bg-no-repeat bg-right"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundSize: '16px',
              backgroundPosition: 'right 4px center',
            }}
          >
            <option value="all">{t('common.status.all')}</option>
            <option value="available">{t('common.status.available')}</option>
          </select>

          {/* Type Filter - Only show if both AC and DC exist */}
          {showTypeFilter && (
            <>
              <button
                onClick={() =>
                  handleTypeChange(filters.type === 'AC' ? 'all' : 'AC')
                }
                className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all ${
                  filters.type === 'AC'
                    ? 'bg-ink text-surface'
                    : 'bg-surface text-ink/80 hover:bg-ink/10'
                }`}
              >
                AC
              </button>
              <button
                onClick={() =>
                  handleTypeChange(filters.type === 'DC' ? 'all' : 'DC')
                }
                className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all ${
                  filters.type === 'DC'
                    ? 'bg-ink text-surface'
                    : 'bg-surface text-ink/80 hover:bg-ink/10'
                }`}
              >
                DC
              </button>
            </>
          )}
        </>
      )}

      {/* Sort Dropdown */}
      <div className="flex-shrink-0">
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value as typeof sortBy)}
          className="text-xs border border-ink/10 rounded-md px-2 py-1.5 bg-surface focus:outline-none focus:ring-1 focus:ring-ink focus:border-transparent appearance-none pr-6 bg-no-repeat bg-right"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
            backgroundSize: '16px',
            backgroundPosition: 'right 4px center',
          }}
        >
          <option value="name">{t('chargePoint.sortByName')}</option>
          <option value="status">{t('chargePoint.sortByStatus')}</option>
          <option value="power">{t('chargePoint.sortByPower')}</option>
          <option value="price">{t('chargePoint.sortByPrice')}</option>
        </select>
      </div>
    </div>
  );
}
